import React, { useState, useEffect } from 'react';
import { Mail, Phone, Linkedin, MapPin, Send, CheckCircle } from 'lucide-react';
import { firmInfo } from '../data/mock';
import { toast } from 'sonner';
import { submitToWeb3Forms } from '../lib/forms';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill from localStorage on mount
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name: localStorage.getItem("lexceed_user_name") || "",
      email: localStorage.getItem("lexceed_user_email") || "",
      phone: localStorage.getItem("lexceed_user_phone") || "",
    }));
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitToWeb3Forms(formData, {
        from_name: "Lexceed Website Contact",
        subject: `New Contact Form Submission: ${formData.subject}`,
      });

      // Save to localStorage
      localStorage.setItem("lexceed_user_name", formData.name);
      localStorage.setItem("lexceed_user_email", formData.email);
      localStorage.setItem("lexceed_user_phone", formData.phone);

      toast.success("Message Sent Successfully", {
        description: "We've received your inquiry and will get back to you shortly.",
      });

      setSubmitted(true);
    } catch (error) {
      toast.error("Submission Error", {
        description: error.message || "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <span className="text-gold text-sm font-medium uppercase tracking-wide">Get In Touch</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
            Let's <span className="text-gold">Connect</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Whether you're navigating a complex transaction, seeking ongoing counsel, or simply
            exploring how LEXCEED can support your business, we are here to help.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-2xl p-8 lg:p-10">
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent rounded-2xl"></div>

                <div className="relative z-10">
                  <h2 className="text-3xl font-serif font-bold text-white mb-6">
                    {submitted ? "Message Sent" : "Send Us a Message"}
                  </h2>

                  {submitted ? (
                    <div className="py-12 text-center animate-in fade-in zoom-in duration-500">
                      <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle size={40} className="text-gold" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                      <p className="text-gray-400 mb-8 max-w-sm mx-auto">
                        We've received your inquiry and will get back to you shortly.
                      </p>
                      <button
                        onClick={() => setSubmitted(false)}
                        className="px-6 py-2 border border-gold text-gold rounded-lg hover:bg-gold hover:text-black transition-colors"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                            placeholder="John Doe"
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                            placeholder="john@company.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                            placeholder="+91 98XXX XXXXX"
                          />
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-2">
                            Company/Organization
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                            placeholder="Your Company"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
                          Subject *
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                          placeholder="How can we help?"
                        />
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                          Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="5"
                          className="w-full px-4 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300 resize-none"
                          placeholder="Tell us about your legal needs..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <span className="animate-spin mr-2">◌</span>
                            Sending...
                          </span>
                        ) : (
                          <>
                            Send Message
                            <Send size={20} className="ml-2" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-serif font-bold text-white mb-6">
                  Contact Information
                </h2>
                <p className="text-gray-400 leading-relaxed mb-8">
                  Prefer to reach out directly? Use any of the contact methods below.
                  We typically respond within 24-48 hours.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <a
                  href={`mailto:${firmInfo.email}`}
                  className="group block bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300"
                >
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                      <Mail size={24} className="text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-gold transition-colors duration-300">
                        Email
                      </h3>
                      <p className="text-gray-400">{firmInfo.email}</p>
                    </div>
                  </div>
                </a>

                <a
                  href={`tel:${firmInfo.phone}`}
                  className="group block bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300"
                >
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                      <Phone size={24} className="text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-gold transition-colors duration-300">
                        Phone
                      </h3>
                      <p className="text-gray-400">{firmInfo.phone}</p>
                    </div>
                  </div>
                </a>

                <a
                  href={firmInfo.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300"
                >
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                      <Linkedin size={24} className="text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-gold transition-colors duration-300">
                        LinkedIn
                      </h3>
                      <p className="text-gray-400">Connect professionally</p>
                    </div>
                  </div>
                </a>

                <div className="group block bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                      <MapPin size={24} className="text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-gold transition-colors duration-300">
                        Location
                      </h3>
                      <p className="text-gray-400">Serving clients globally</p>
                      <p className="text-gray-500 text-sm mt-1">Based in India</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
